import React, { useState, useEffect, useCallback } from 'react';
import Tube from './Tube';
import WinModal from './WinModal';
import { generateLevel, canMove, moveBall, checkWin, deepClone, getLevelConfig } from '../utils/gameLogic';
import { playMove, playSelect, playError, playWin } from '../utils/sounds';
import './GameBoard.css';

export default function GameBoard() {
  const [level, setLevel] = useState(1);
  const [tubes, setTubes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [moves, setMoves] = useState(0);
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [history, setHistory] = useState([]);
  const [won, setWon] = useState(false);
  

  const { capacity } = getLevelConfig(level);

  const initLevel = useCallback((lvl) => {
    const t = generateLevel(lvl);
    setTubes(t);
    setSelected(null);
    setMoves(0);
    setScore(0);
    setHistory([]);
    setWon(false);
  }, []);

  useEffect(() => { initLevel(level); }, [level, initLevel]);

  const handleTubeClick = (idx) => {
    if (won) return;

    if (selected === null) {
      if (tubes[idx].length === 0) return;
      playSelect();
      setSelected(idx);
    } else {
      if (selected === idx) {
        setSelected(null);
        return;
      }
      if (canMove(tubes, selected, idx, capacity)) {
        const prevTubes = deepClone(tubes);
        setHistory(h => [...h.slice(-29), prevTubes]);
        const newTubes = moveBall(tubes, selected, idx);
        setTubes(newTubes);
        setSelected(null);
        setMoves(m => m + 1);
        playMove();

        if (checkWin(newTubes, capacity)) {
          const bonus = Math.max(0, 1000 - moves * 10);
          const lvlScore = bonus + level * 500;
          setScore(lvlScore);
          setTotalScore(ts => ts + lvlScore);
          setTimeout(() => { setWon(true); playWin(); }, 300);
        }
      } else {
        playError();
        
        
        if (tubes[idx].length > 0) {
          playSelect();
          setSelected(idx);
        } else {
          setSelected(null);
        }
      }
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setTubes(prev);
    setHistory(h => h.slice(0, -1));
    setMoves(m => Math.max(0, m - 1));
    setSelected(null);
  };

  const handleRestart = () => { initLevel(level); };
  const handleNextLevel = () => { setLevel(l => l + 1); };

  const cols = tubes.length <= 4 ? tubes.length
    : tubes.length <= 6 ? Math.ceil(tubes.length / 2)
    : tubes.length <= 9 ? Math.ceil(tubes.length / 2)
    : Math.ceil(tubes.length / 3);

  return (
    <div className="game-board">
      {/* HUD */}
      <div className="hud">
        <div className="hud-item">
          <span className="hud-icon">🏆</span>
          <span className="hud-val">{totalScore.toLocaleString()}</span>
          <span className="hud-label">Score</span>
        </div>
        <div className="hud-center">
          <div className="level-badge">Level {level}</div>
          <div className="move-count">{moves} moves</div>
        </div>
        <div className="hud-item right">
          <span className="hud-icon">👣</span>
          <span className="hud-val">{moves}</span>
          <span className="hud-label">Moves</span>
        </div>
      </div>

      {/* Tubes */}
      <div className="tubes-area">
        <div className="tubes-grid" style={{ '--cols': Math.min(cols, tubes.length) }}>
          {tubes.map((tube, i) => (
            <Tube
              key={i}
              tubeIndex={i}
              balls={tube}
              capacity={capacity}
              isSelected={selected === i}
              isHint={false}
              onClick={() => handleTubeClick(i)}
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="controls">
        <button className="ctrl-btn undo" onClick={handleUndo} disabled={history.length === 0}>
          <span>↩</span> Undo
        </button>
        <button className="ctrl-btn restart" onClick={handleRestart}>
          <span>🔄</span> Restart
        </button>
        <button className="ctrl-btn next-lvl" onClick={handleNextLevel}>
          <span>⏭</span> Skip
        </button>
      </div>

      {won && (
        <WinModal
          level={level}
          moves={moves}
          score={score}
          onNextLevel={handleNextLevel}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
